import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLogout } from "../hooks/useLogout";
import { useAuthContext } from "../hooks/useAuthContext";
import { useCompanyContext } from "../hooks/useCompanyContext";
import { useCultivationCompanyContext } from "../hooks/useCultivationCompanyContext"; // ✅ Cultivation context importeren
import { useLanguageContext } from "../hooks/useLanguageContext";
import translations from "../translations";

const Navbar = () => {
  const { logout } = useLogout();
  const { user } = useAuthContext();
  const { company, dispatch } = useCompanyContext();
  const { cultivationCompany } = useCultivationCompanyContext(); // ✅ Cultivation company ophalen
  const [companies, setCompanies] = useState([]);
  const { language, setLanguage } = useLanguageContext();

  useEffect(() => {
    if (user?.companies) {
      const visibleCompanies = user.companies.filter(
        (company) => company.archived !== true
      );
  
      const sortedCompanies = visibleCompanies.sort((a, b) =>
        a.Name.localeCompare(b.Name)
      );
  
      setCompanies(sortedCompanies);
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    if (user?.cultivation_company?.name === "WPK") {
      window.location.href = "/login?ref=wpk";
    } else if (user?.cultivation_company?.name === "P. van Geest") {
      window.location.href = "/login?ref=pvangeest";
    } else {
      window.location.href = "/login";
    }
  };

  const handleCompanySelect = async (event) => {
    const selectedCompanyId = event.target.value;
    const storageKey = `selectedCompany:${user?.email?.toLowerCase()}`;
  
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/bedrijf/${selectedCompanyId}`,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );
  
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
  
      const fullCompany = await response.json();
  
      // 1) Update context met het VOLLEDIGE bedrijf (incl. companyinfo/contacts/etc.)
      dispatch({ type: "SET_COMPANY", payload: fullCompany });
  
      // 2) Opslaan in sessionStorage: alleen een minimale snapshot
      const minimal = {
        Companyid: fullCompany.Companyid,
        Name: fullCompany.Name,
      };
      sessionStorage.setItem(storageKey, JSON.stringify(minimal));
      sessionStorage.setItem("selectedCompany", JSON.stringify(minimal)); // tijdelijk voor legacy lezers
  
    } catch (error) {
      console.error("❌ Fout bij ophalen bedrijf:", error);
    }
  };
  
    // ✅ Role helpers
  const roles = Array.isArray(user?.roles) ? user.roles : [];

  const canAdministratie     = roles.includes('Administratie');
  const canOrdersGoedkeuren  = roles.includes('Orders goedkeuren');
  const canTeelt             = roles.includes('Teelt');
  const canTransport         = roles.includes('Transport');
  const canDossierTransport  = roles.includes('actorius');
  // Alleen tonen als zowel rol aanwezig is als de cultivation company "WPK" is
  const canDossierTeelt      = roles.includes('Dossier Teelt') && cultivationCompany?.name === 'WPK';
  const canLetsGrow      = cultivationCompany?.name === 'P. van Geest';

  // Beheer blijft zoals je al had:
  const canBeheer = (roles.includes('medewerker') || user?.email === 'Brigitte.Akkermans@wpk.nl');
  const canEvaluaties = roles.includes('medewerker');


  console.log("User:", user);

  return (
    <header>
      <div className="navbar-container">
        <div className="navbar-left">
          <Link to="/">
            <img
              src={
                user?.cultivation_company?.name === "WPK"
                  ? "/wpk-logo.svg"
                  : user?.cultivation_company?.name === "P. van Geest"
                  ? "/p-van-geest-plantenkwekerij-logo_100px.png"
                  : "/assets/logos/default.png"
              }
              alt="Logo"
              className="navbar-logo"
              height="120px"
            />
          </Link>
        </div>

        <div className="navbar-right">
          {user && (
            <div className="user-info">
              <span className="user-name">{user.name}</span>
              <select
                className="company-select"
                onChange={handleCompanySelect}
                value={company?.Companyid || ""}
              >
                <option value="" disabled>
                  Selecteer een bedrijf
                </option>
                {companies.length > 0 ? (
                  companies.map((company) => (
                    <option key={company.Companyid} value={company.Companyid}>
                      {company.Name}
                    </option>
                  ))
                ) : (
                  <option disabled>Geen bedrijven beschikbaar</option>
                )}
              </select>
              <select 
                className="language-select" 
                value={language} 
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="nl">Nederlands</option>
                <option value="en">English</option>
                <option value="de">Deutsch</option>
                <option value="fr">Français</option>
              </select>
              <button onClick={handleLogout} className="logout-button">
                {translations[language].uitloggen}
              </button>
            </div>
          )}
          {!user && (
            <div>
              <Link to="/login">Inloggen</Link>
              <Link to="/signup">Registreren</Link>
            </div>
          )}
        </div>
      </div>
      <nav className="main-menu">
        <ul>
          {canAdministratie && (
            <li>
              <Link to="/">{translations[language].administratie}</Link>
            </li>
          )}

          {canOrdersGoedkeuren && (
            <li>
              <Link to="/orders-goedkeuren">{translations[language].ordersGoedkeuren}</Link>
            </li>
          )}

          {canTeelt && (
            <li>
              <Link to="/teelt">{translations[language].teelt}</Link>
            </li>
          )}

          {canDossierTeelt && (
            <li>
              <Link to="/dossierteelt">{translations[language].dossierTeelt}</Link>
            </li>
          )}

          {canTransport && (
            <li>
              <Link to="/transport">{translations[language].transport}</Link>
            </li>
          )}

          {canDossierTransport && (
            <li>
              <Link to="/dossiertransport">{translations[language].dossierTransport}</Link>
            </li>
          )}

          {canEvaluaties && (
            <li>
              <Link to="/evaluaties">Evaluaties</Link>
            </li>
          )}

          {canBeheer && (
            <li>
              <Link to="/beheer">Beheer</Link>
            </li>
          )}

          {canLetsGrow && ( 
          <li>
            <a
              className="btn btn-default"
              style={{ paddingTop: "1px", paddingBottom: "1px", marginTop: "2px", marginLeft: "2px", width: "75px" }}
              href="https://my.letsgrow.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/letsgrow_logo.jpeg"
                alt="Let's Grow"
                style={{ float: "left", height: "25px", width: "43px" }}
              />
            </a>
          </li>
          )}
        </ul>
      </nav>
      {canAdministratie && (
      <nav className="sub-menu">
        <ul>
          <li className="active">
            <Link to="/">{translations[language].klantgegevens}</Link>
          </li>
          <li>
            <Link to="/orderinformatie">{translations[language].orderinformatie}</Link>
          </li>
          <li>
            <Link to="/facturen">{translations[language].facturen}</Link>
          </li>
        </ul>
      </nav>
      )}
    </header>
  );
};

export default Navbar;
